// Version management system
export interface VersionInfo {
  version: string
  buildDate: string
  buildNumber: number
  changelog: string[]
  features: string[]
  fixes: string[]
}

// Current version - update this when making significant changes
export const CURRENT_VERSION = '1.3.0'

// Build information
export const BUILD_DATE = new Date().toISOString().split('T')[0]
export const BUILD_NUMBER = 130 // Increment with each version

// Version history and changelog
export const VERSION_HISTORY: Record<string, VersionInfo> = {
  '1.0.0': {
    version: '1.0.0',
    buildDate: '2024-01-01',
    buildNumber: 100,
    changelog: [
      'Initial release of Grow a Garden Tracker',
      'Real-time stock tracking with rarity information',
      'Weather monitoring with effects and strategies',
      'Comprehensive game guide system',
      'Dashboard with market insights and pro tips',
      'Modern dark theme with glass morphism effects'
    ],
    features: [
      'Stock Tracker with real-time updates',
      'Weather Tracker with effects analysis',
      'Game Guide with comprehensive strategies',
      'Dashboard with overview and insights',
      'Settings page with API configuration',
      'Modern UI with dark theme'
    ],
    fixes: [
      'Initial release - no fixes needed'
    ]
  },
     '1.1.0': {
     version: '1.1.0',
     buildDate: '2025-08-03',
     buildNumber: 110,
    changelog: [
      'Enhanced theme system with light/dark mode support',
      'System theme detection and automatic switching',
      'Theme persistence with localStorage',
      'Improved typography and readability across themes',
      'Smooth theme transitions and animations',
      'Theme-aware components throughout the application'
    ],
    features: [
      'Three theme modes: System, Light, and Dark',
      'ThemeToggle component with dropdown interface',
      'ThemeIndicator component for status display',
      'Automatic theme persistence',
      'System theme detection and following',
      'Enhanced glass morphism for both themes'
    ],
    fixes: [
      'Fixed title readability in light mode',
      'Improved text contrast across all themes',
      'Enhanced navigation text visibility',
      'Fixed card title styling in light theme',
      'Improved error boundary text visibility'
    ]
  },
  '1.1.1': {
    version: '1.1.1',
    buildDate: '2025-08-03',
    buildNumber: 111,
    changelog: [
      'Version 1.1.1 - Traveling Merchant and Mutation Weather synchronization',
      'Synchronized traveling merchant availability with in-game appearing time',
      'Enhanced merchant schedule tracking with real-time countdown',
      'Added merchant arrival and departure notifications',
      'Synchronized mutation weather patterns with in-game timing',
      'Enhanced mutation weather tracking with peak timing calculations',
      'Added mutation weather notifications and status display',
      'Improved weather tracker UI with mutation synchronization'
    ],
    features: [
      'Real-time traveling merchant schedule synchronization',
      'Merchant arrival countdown with accurate timing',
      'Automatic merchant status updates and notifications',
      'Real-time mutation weather schedule synchronization',
      'Mutation weather peak timing calculations and predictions',
      'Enhanced mutation weather display with active/inactive states',
      'Improved weather tracking with mutation integration',
      'Better mutation timing calculations and notifications'
    ],
    fixes: [
      'Fixed merchant availability timing synchronization',
      'Improved merchant status display accuracy',
      'Enhanced merchant notification system',
      'Fixed mutation weather timing synchronization',
      'Improved mutation weather display accuracy',
      'Enhanced mutation weather notification system',
      'Fixed mutation schedule calculation precision',
      'Improved mutation UI responsiveness and reliability'
    ]
  },
  '1.2.0': {
    version: '1.2.0',
    buildDate: '2025-08-03',
    buildNumber: 120,
    changelog: [
      'Version 1.2.0 - Event Tracker and comprehensive game synchronization',
      'Added comprehensive Event Tracker with real-time event monitoring',
      'Integrated event timing synchronization with in-game event schedule',
      'Enhanced event strategy system with weather compatibility analysis',
      'Added event-specific tips and optimal participation strategies',
      'Implemented event notifications for start, end, and upcoming events',
      'Created event rewards and requirements tracking system',
      'Added upcoming events preview and countdown timers'
    ],
    features: [
      'Comprehensive Event Tracker with real-time synchronization',
      'Event timing calculations and countdown timers',
      'Event-specific strategies and weather compatibility analysis',
      'Smart event notifications and alerts',
      'Event rewards and requirements tracking',
      'Upcoming events preview and scheduling',
      'Weather-optimized event participation recommendations',
      'Event strategy tips and optimal conditions guidance'
    ],
    fixes: [
      'Enhanced API integration with current event endpoint',
      'Improved event timing accuracy and synchronization',
      'Added comprehensive event data handling and display',
      'Enhanced user experience with event-specific guidance',
      'Improved notification system for event-related alerts',
      'Better event strategy recommendations and weather analysis'
    ]
  },
     '1.2.1': {
     version: '1.2.1',
     buildDate: '2025-08-03',
     buildNumber: 121,
     changelog: [
       'Version 1.2.1 - Cooking Event synchronization and comprehensive recipe tracking',
       'Synchronized Event Tracker with current Cooking Event (August 2-16, 2025)',
       'Added comprehensive cooking recipes database with confirmed and unconfirmed recipes',
       'Integrated cooking rewards system with rarity-based reward tiers',
       'Enhanced event strategy with cooking-specific tips and mechanics',
       'Added cooking event recipes with ingredient requirements and confirmation status',
       'Implemented cooking rewards tracking from Common to Prismatic tiers',
       'Updated event detection to automatically identify cooking-related events'
     ],
     features: [
       'Cooking Event synchronization with real-time event data',
       'Comprehensive cooking recipes database with 11 recipe types',
       'Cooking rewards system with 7 rarity tiers',
       'Recipe confirmation status tracking (confirmed/unconfirmed)',
       'Cooking-specific event strategies and tips',
       'Automatic cooking event detection and display',
       'Ingredient requirement tracking for all recipes',
       'Rarity-based reward categorization and display'
     ],
     fixes: [
       'Enhanced event detection for cooking-related events',
       'Improved recipe display with confirmation status indicators',
       'Added comprehensive cooking event data from official wiki',
       'Enhanced user experience with cooking-specific guidance',
       'Improved event strategy recommendations for cooking mechanics',
       'Better integration with current game events and schedules'
     ]
   },
   '1.2.2': {
     version: '1.2.2',
     buildDate: BUILD_DATE,
     buildNumber: 122,
     changelog: [
       'Version 1.2.2 - Cooking Recipe UI/UX Redesign and Optimization',
       'Completely redesigned cooking recipe interface with modern card-based layout',
       'Removed debug information and unnecessary event tracking sections',
       'Simplified interface to focus on essential cooking recipe functionality',
       'Removed search functionality and time/difficulty information for cleaner UI',
       'Removed cooking rewards section to streamline the interface',
       'Enhanced rarity tier navigation with consistent colored dots for all tiers',
       'Optimized layout with sidebar + main content area for better space utilization',
       'Improved recipe filtering with confirmed-only toggle and rarity selection',
       'Streamlined recipe cards with essential information only'
     ],
     features: [
       'Modern card-based cooking recipe interface with improved visual hierarchy',
       'Compact sidebar with recipe info and rarity navigation',
       'Enhanced rarity tier buttons with consistent colored indicators',
       'Simplified recipe filtering system (recipe type, rarity, confirmation status)',
       'Responsive grid layout for recipe cards with hover effects',
       'Clean, focused interface without unnecessary information',
       'Improved mobile responsiveness and touch interactions',
       'Streamlined user experience with essential features only'
     ],
     fixes: [
       'Removed debug information cluttering the interface',
       'Eliminated unnecessary event stats and strategy sections',
       'Removed search functionality to simplify the interface',
       'Removed time and difficulty information for cleaner recipe cards',
       'Removed cooking rewards section to focus on recipes',
       'Fixed rarity tier dot visibility for all tiers',
       'Improved overall interface cleanliness and usability',
       'Enhanced performance by removing unused components'
     ]
    },
  
  '1.3.0': {
    version: '1.3.0',
    buildDate: '2025-08-04',
    buildNumber: 130,
    changelog: [
      'Version 1.3.0 - Comprehensive Pet Wiki System',
      'Added complete Pet Wiki with 59 pets (44 obtainable, 15 unobtainable)',
      'Implemented tabbed navigation system (Overview, Pets, Mechanics, Mutations)',
      'Created separated display for obtainable and unobtainable pets with visual indicators',
      'Added comprehensive pet data including abilities, stats, and acquisition methods',
      'Implemented advanced filtering system (rarity, ability category, search)',
      'Added detailed pet mechanics documentation (age system, weight system, hunger system)',
      'Created pet mutations section with odds and effects information',
      'Implemented responsive pet cards with rarity badges and status indicators',
      'Added detailed pet modal with comprehensive information display',
      'Created dedicated CSS styling system for pet page components'
    ],
    features: [
      'Complete Pet Wiki with comprehensive pet database',
      'Tabbed navigation system for organized content browsing',
      'Separated obtainable/unobtainable pet sections with visual indicators',
      'Advanced search and filtering capabilities',
      'Detailed pet mechanics documentation and guides',
      'Pet mutations system with odds and effects',
      'Responsive pet cards with rarity and status badges',
      'Comprehensive pet details modal with stats and abilities',
      'Dedicated CSS styling system for consistent pet page design',
      'Event-exclusive pets tracking (Christmas, Easter, Halloween, Valentine\'s, etc.)',
      'Real-time pet data integration with API context',
      'Modern UI with glass morphism effects and smooth animations'
    ],
    fixes: [
      'Fixed hunger rate display issue in pet details modal',
      'Improved pet data structure and type safety',
      'Enhanced responsive design for mobile devices',
      'Optimized pet card performance with proper filtering',
      'Fixed CSS class organization for better maintainability',
      'Improved accessibility with proper ARIA labels and semantic HTML',
      'Enhanced error handling for pet data loading',
      'Fixed version management system integration'
    ]
  },}

// Get current version info
export function getCurrentVersionInfo(): VersionInfo {
  return VERSION_HISTORY[CURRENT_VERSION] || {
    version: CURRENT_VERSION,
    buildDate: BUILD_DATE,
    buildNumber: BUILD_NUMBER,
    changelog: ['Version information not available'],
    features: [],
    fixes: []
  }
}

// Get version info for a specific version
export function getVersionInfo(version: string): VersionInfo | null {
  return VERSION_HISTORY[version] || null
}

// Get all available versions
export function getAllVersions(): string[] {
  return Object.keys(VERSION_HISTORY).sort((a, b) => {
    const versionA = VERSION_HISTORY[a].buildNumber
    const versionB = VERSION_HISTORY[b].buildNumber
    return versionB - versionA // Sort in descending order
  })
}

// Check if version is latest
export function isLatestVersion(version: string): boolean {
  return version === CURRENT_VERSION
}

// Get version comparison
export function compareVersions(version1: string, version2: string): number {
  const v1 = VERSION_HISTORY[version1]?.buildNumber || 0
  const v2 = VERSION_HISTORY[version2]?.buildNumber || 0
  return v1 - v2
}

// Format version for display
export function formatVersion(version: string): string {
  const info = VERSION_HISTORY[version]
  if (!info) return version
  
  return `${info.version} (Build ${info.buildNumber})`
}

// Get recent changes (last 3 versions)
export function getRecentChanges(): VersionInfo[] {
  const versions = getAllVersions().slice(0, 3)
  return versions.map(version => VERSION_HISTORY[version])
} 